import { FilterBar, FilterPill, Input, MMButton } from '@components'
import cn from '@integrations/classnames'
import { getRecurringFrequencyOptions } from '@utils'
import { RecurringPlanStatuses } from 'mmfintech-backend-api'
import type { RecurringPlanFilter } from 'mmfintech-backend-api/types'
import { tr } from 'mmfintech-commons'
import type { UseFilterOutput } from 'mmfintech-commons/types'

import styles from './RecurringPaymentsFilter.module.scss'

interface Props {
  filter: UseFilterOutput<RecurringPlanFilter>
}

function getFrequencyFilterOptions() {
  return [
    { label: tr('FRONTEND.RECURRING.FILTER.ALL_FREQUENCIES', 'All frequencies'), value: '' },
    ...getRecurringFrequencyOptions()
  ]
}

function getStatusFilterOptions() {
  return [
    { label: tr('FRONTEND.RECURRING.FILTER.ALL_STATUSES', 'All statuses'), value: '' },
    { label: tr('FRONTEND.RECURRING.STATUS.ACTIVE', 'Active'), value: RecurringPlanStatuses.ACTIVE },
    { label: tr('FRONTEND.RECURRING.STATUS.PAUSED', 'Paused'), value: RecurringPlanStatuses.PAUSED },
    { label: tr('FRONTEND.RECURRING.STATUS.CANCELLED', 'Cancelled'), value: RecurringPlanStatuses.CANCELLED }
  ]
}

function RecurringPaymentsFilter({ filter }: Props) {
  const selectedStatus = ((filter.get('recurringPlanStatuses') as string[]) || [])[0] || ''

  function handleStatusChange(_name: string, value: string) {
    if (value) {
      filter.set('recurringPlanStatuses', [value])
    } else {
      // prepare() only excludes empty strings/non-positive numbers, not empty arrays,
      // so setting [] here would still send recurringPlanStatuses: [] to the backend
      // (matching zero statuses) instead of omitting the filter entirely.
      filter.deleteValue('recurringPlanStatuses')
    }
  }

  return (
    <FilterBar.Row
      className={cn(styles['recurring-payments-filter'], 'recurring-payments-filter', 'filters-container')}
      data-test='recurring-payments-filter'
    >
      <FilterPill
        label={tr('FRONTEND.RECURRING.FILTER.FREQUENCY', 'Frequency')}
        active={Boolean(filter.get('recurringStep'))}
      >
        <Input
          type='select'
          name='recurringStep'
          options={getFrequencyFilterOptions()}
          {...filter.registerInput('recurringStep')}
          parentClassName={cn('transaction-filter-field-md', 'transaction-filter-select')}
          data-test='filter-recurring-frequency'
          hideErrorLine
          isSearchable={false}
          dropdownPortal
        />
      </FilterPill>
      <FilterPill label={tr('FRONTEND.RECURRING.FILTER.STATUS', 'Status')} active={Boolean(selectedStatus)}>
        <Input
          type='select'
          name='recurringPlanStatuses'
          options={getStatusFilterOptions()}
          value={selectedStatus}
          onChange={handleStatusChange}
          parentClassName={cn('transaction-filter-field-md', 'transaction-filter-select')}
          data-test='filter-recurring-status'
          hideErrorLine
          isSearchable={false}
          dropdownPortal
        />
      </FilterPill>
      <FilterBar.End>
        <MMButton
          type='button'
          variant='secondary'
          caption={tr('FRONTEND.RECURRING.FILTER.RESET_BUTTON', 'Reset')}
          onClick={() => filter.reset()}
          className={cn(styles['recurring-payments-reset'], 'recurring-payments-reset')}
          data-test='filter-recurring-reset'
        />
      </FilterBar.End>
    </FilterBar.Row>
  )
}

export default RecurringPaymentsFilter
